import type { InsightsReportResource } from "@octopusdeploy/octopus-server-client";
import type { PageRouteDefinition, QueryParamValues, QueryParamValuesSetter, UnknownQueryParam } from "@octopusdeploy/portal-routes";
import type { ReactElement } from "react";
import type { Loader } from "~/routing/pageRegistrations/Loader";
import type { LoadingContentRender, PageRegistration } from "~/routing/pageRegistrations/PageRegistration";
import type { StandardLoaderContext } from "~/routing/pageRegistrations/StandardLoaderContext";
import type { StandardPageContext } from "./StandardPageContext";
import type { PageOwner } from "./pageOwner";
export function createInsightsPageRegistration<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData>(route: PageRouteDefinition<RouteParams, QueryParams>, config: {
    render: (parameters: {
        context: StandardPageContext;
        loaderData: LoaderData;
        routeParams: RouteParams;
        queryParams: QueryParamValues<QueryParams>;
        setQueryParams: QueryParamValuesSetter<QueryParamValues<QueryParams>>;
    }) => ReactElement;
    pageId: string;
    pageName: string;
    pageArea: string;
    owner: PageOwner;
    loader?: Loader<LoaderData, StandardLoaderContext, RouteParams, QueryParams>;
    loadingTitle?: string;
    loadingContent?: LoadingContentRender;
    helpContent?: () => ReactElement;
    helpTopics?: () => ReactElement;
}): PageRegistration<RouteParams, QueryParams, LoaderData, StandardLoaderContext, StandardPageContext> {
    return {
        route,
        render: config.render,
        pageId: config.pageId,
        pageName: config.pageName,
        pageArea: config.pageArea,
        owner: config.owner,
        verticalNavigationPageArea: "Insights",
        searchKeywords: [],
        loader: config.loader,
        loadingTitle: config.loadingTitle,
        loadingContent: config.loadingContent,
        helpContent: config.helpContent,
        helpTopics: config.helpTopics,
        level: 1,
        isFullWidth: true,
    };
}
export function createInsightsReportPageRegistration<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData>(route: PageRouteDefinition<RouteParams, QueryParams>, config: {
    render: (parameters: {
        context: InsightsReportPageContext;
        loaderData: LoaderData;
        routeParams: RouteParams;
        queryParams: QueryParamValues<QueryParams>;
        setQueryParams: QueryParamValuesSetter<QueryParamValues<QueryParams>>;
    }) => ReactElement;
    loader?: Loader<LoaderData, StandardLoaderContext, RouteParams, QueryParams>;
    loadingTitle?: string;
    loadingContent?: LoadingContentRender;
    helpContent?: () => ReactElement;
    helpTopics?: () => ReactElement;
    pageId: string;
    pageName: string;
    pageArea: string;
    owner: PageOwner;
}): PageRegistration<RouteParams, QueryParams, LoaderData, StandardLoaderContext, InsightsReportPageContext> {
    return {
        route,
        render: config.render,
        loader: config.loader,
        loadingTitle: config.loadingTitle,
        pageId: config.pageId,
        pageName: config.pageName,
        pageArea: config.pageArea,
        owner: config.owner,
        verticalNavigationPageArea: "Insights",
        searchKeywords: [],
        loadingContent: config.loadingContent,
        helpContent: config.helpContent,
        helpTopics: config.helpTopics,
        level: 2,
        isFullWidth: false,
    };
}
export interface InsightsReportPageContext extends StandardPageContext {
    report: InsightsReportResource;
    refreshReport: () => Promise<void>;
}
